import { Box, Text, Flex, VStack, Image } from '@chakra-ui/react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'next-i18next'
import React, {useCallback, useEffect} from 'react'
import { useFormContext } from 'react-hook-form'
import { get } from 'lodash-es'

import FormErrorMessage from '_app/components/Form/FormErrorMessage'
import { SendDataIcon } from '_app/components/Icons'
import theme from 'theme'
import toast from '_app/utils/toast'

interface ImageDropzoneProps {
  name: string;
  label: string;
  accept: string[];
  onAddHandler: VoidFunction;
  onRemoveHandler: VoidFunction;
  maxSize?: number;
}

const ImageDropzone = ({
  name,
  label,
  accept,
  onAddHandler,
  onRemoveHandler,
  maxSize,
}: ImageDropzoneProps) => {
  const { t } = useTranslation('dropzone')
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext()

  const removeFile = () => {
    setValue(name, null)
    onRemoveHandler()
  }

  const onDrop = useCallback(
    droppedFiles => {
      setValue(name, droppedFiles)
      onAddHandler()
    },
    [name, setValue]
  )
  const formats = accept
    .map(format => format.split('/')[1]?.toUpperCase())
    .join(', ')

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      accept,
      onDropRejected: errors => {
          const listErrors = errors[0].errors
          listErrors?.forEach((el: any) => {
              if (el?.message) {
                  if (el?.code === 'file-too-large') {
                      toast({
                          description: `${t('DROPZONE_SIZE')}: ${(Number(maxSize) / (1024 * 1024)).toFixed(2)} MB`,
                          status: 'error',
                      })
                  } else {
                      toast({
                          description: el?.message,
                          status: 'error',
                      })
                  }
              }
        })
      },
      onDrop,
      multiple: false,
      maxSize,
    })

  const errorMessage: any = get(errors, name)?.message

  const files = watch(name)

  const file = files?.[0]
    ? { ...files[0], preview: URL.createObjectURL(files?.[0]) }
    : null


    useEffect(() => {
        return () => {
            if (file?.preview) {
                URL.revokeObjectURL(file.preview)
            }
        }
    }, [file])

  return (
    <Box width="100%">
      <Flex w="full" gap={4}>
        {file && (
          <Box display="flex" flexDirection="column">
            <Box
              position="relative"
              borderWidth="1px"
              w="130px"
              minW="130px"
              h="130px"
            >
              <Image
                display="block"
                src={file.preview}
                alt={file.path}
                title={file.path}
                w="100%"
                h="100%"
              />
            </Box>
            <Box
              role="button"
              onClick={removeFile}
              textAlign="center"
              fontSize="12px"
            >
              {t('DELETE_ITEM')}
            </Box>
          </Box>
        )}

        {!file && (
          <Box width="100%">
            <div
              {...getRootProps({
                style: {
                  width: '100%',
                },
              })}
            >
              <input
                {...register(name)}
                name={name}
                data-testid={name}
                style={{ display: 'block' }}
                {...getInputProps()}
                onChange={getInputProps().onChange}
              />

              <VStack
                justifyContent="center"
                borderWidth="1px"
                borderColor={isDragReject ? 'red' : 'black'}
                borderStyle={isDragActive ? 'solid' : 'dashed'}
                py="2.25rem"
                w="full"
              >
                <SendDataIcon />
                <Text fontWeight={300}>{label}</Text>
              </VStack>
            </div>
            <Text
              fontSize={theme.textStyles.fontSizes.xs}
              fontWeight={300}
              pt={2}
            >
              {t('DROPZONE_DISCLAIMER')}
            </Text>
          </Box>
        )}
      </Flex>
      <FormErrorMessage name={t('FILE')}>{errorMessage}</FormErrorMessage>
    </Box>
  )
}

export default ImageDropzone
